import { useEffect, useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Tabs, EVENT_NAME } from 'common/constants';
import { ComposedTable } from 'components';
import { useAmplitude } from 'components/AmplitudeProvider';
import { useClient } from 'components/ClientProvider';
import { ScrollToTopButton } from 'components/ScrollToTopButton/ScrollToTopButton';
import { SkeletonPageLayout } from 'components/SkeletonLoader/SkeletonPageLayout';
import { getSelectedStoreOption } from 'components/StoreSelect/slice';
import {
  getSelectedTab,
  getSelectedTabToBePaid
} from 'pages/LandingPage/components/LandingPageHeader/slice';
import {
  PaymentMethodChips,
  VendorsTableHeader
} from 'pages/VendorsTablePage/components';
import { labels } from 'pages/VendorsTablePage/messages';
import { StyledCard } from 'pages/VendorsTablePage/styled';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { VendorsTotalsFooter } from '../VendorsTotalsFooter/VendorsTotalsFooter';
import { DEFAULT_COL_WIDTH, MIN_COL_WIDTH } from './constants';
import { getNumberFromCurrency } from './helpers';
import {
  getDueDateInFormatToDisplay,
  getPaymentMethods,
  getSearchInputValue,
  getSelectedTotal,
  getSelectedTotalDiscount,
  getVendorsResponse,
  setIsLoadingVendors,
  setDebouncedSearchInputValue,
  setSearchInputValue,
  getRowDataVendorsLength,
  setFullPageLoader,
  getShowFullPageLoaderValue
} from './slice';
import { StyledTableHeader } from './styled';
import { VendorsTableDataResult, VendorsTableDataType } from './types';
import { useVendorsTableColumnDefs } from './useVendorsTableColumnDefs';
import { useVendorsTableData } from './useVendorsTableData';

export const VendorsTable = (): React.JSX.Element => {
  const amplitude = useAmplitude();
  const [trackLaunchOfToBePaid, setTrackLaunchOfToBePaid] = useState(false);
  const dispatch = useAppDispatch();
  const client = useClient();
  const selectedTabToBePaid = useAppSelector(getSelectedTabToBePaid);
  const searchInputValue = useAppSelector(getSearchInputValue);
  const selectedTotalInFooter = useAppSelector(getSelectedTotal);
  const discountTotalInFooter = useAppSelector(getSelectedTotalDiscount);
  const totalOwedInFooter =
    getNumberFromCurrency(useAppSelector(getVendorsResponse)?.totalOwed) -
    selectedTotalInFooter;
  const selectedStore = useAppSelector(getSelectedStoreOption).label;
  const selectedDueDate = useAppSelector(getDueDateInFormatToDisplay);
  const selectedPaymentMethods = useAppSelector(getPaymentMethods).join(',');
  const rowDataLength = useAppSelector(getRowDataVendorsLength);
  const showFullPageLoader = useAppSelector(getShowFullPageLoaderValue);
  const selectedTab = useAppSelector(getSelectedTab);
  const intl: IntlShape = useIntl();
  const ampFlagForNeedsAuth = useRef<boolean>(true);

  const {
    rowData,
    data,
    isLoading,
    isError,
    isSuccess
  }: VendorsTableDataResult = useVendorsTableData(true);

  const { autoGroupColumnDef, columnDefs } = useVendorsTableColumnDefs();

  const onSearchInputChange = (newValue: string): void => {
    dispatch(setSearchInputValue(newValue));
    if (selectedTab === Tabs.ToBePaid) {
      amplitudeTracker(amplitude, EVENT_NAME.SearchVendorInvoices);
    }
  };

  const onDebouncedSearchInputChange = (searchValue: string) => {
    dispatch(setDebouncedSearchInputValue(searchValue));
  };

  useEffect(() => {
    dispatch(setIsLoadingVendors(isLoading));
  }, [dispatch, isLoading]);
  useEffect(() => {
    if (isSuccess || isError) dispatch(setFullPageLoader(false));
  }, [dispatch, isSuccess, isError]);

  useEffect(() => {
    if (amplitude && !trackLaunchOfToBePaid && selectedTab === Tabs.ToBePaid) {
      amplitudeTracker(amplitude, EVENT_NAME.ToBePaidTab);
      amplitudeTracker(amplitude, EVENT_NAME.EnterAccountsPayable);
      setTrackLaunchOfToBePaid(true);
    }
  }, [amplitude, trackLaunchOfToBePaid, setTrackLaunchOfToBePaid, selectedTab]);

  useEffect(() => {
    if (
      amplitude &&
      ampFlagForNeedsAuth.current &&
      selectedTab === Tabs.NeedsAuthorization
    ) {
      amplitudeTracker(amplitude, EVENT_NAME.ReviewAuthorization);
      ampFlagForNeedsAuth.current = false;
    }
  }, [amplitude, trackLaunchOfToBePaid, setTrackLaunchOfToBePaid, selectedTab]);

  useEffect(() => {
    dispatch(setSearchInputValue(''));
    dispatch(setDebouncedSearchInputValue(''));
  }, [dispatch, selectedTab]);

  return isLoading && !showFullPageLoader ? (
    <SkeletonPageLayout />
  ) : (
    <>
      <ScrollToTopButton />
      <StyledCard>
        <ComposedTable<VendorsTableDataType>
          autoGroupColumnDef={autoGroupColumnDef}
          columnDefs={columnDefs}
          defaultColDef={{
            width: DEFAULT_COL_WIDTH,
            minWidth: MIN_COL_WIDTH
          }}
          downloadDialogTitle={intl.formatMessage(
            labels.needsAuthorizationTitle
          )}
          customHeight={client?.isMFE ? 'calc(100vh - 230px)' : undefined}
          getRowId={param => param.data?.id}
          isDownloadable={!isLoading && !selectedTabToBePaid}
          isLoading={isLoading}
          isPrintable={!isLoading && !selectedTabToBePaid}
          isSearchable={!isLoading}
          showLoader={isLoading && showFullPageLoader}
          onDebouncedSearchInputChange={onDebouncedSearchInputChange}
          onSearchInputChange={onSearchInputChange}
          rowData={rowData}
          scrollToTopButtonShow={false}
          searchInputValue={searchInputValue}
          suppressAggFuncInHeader
          suppressMaintainUnsortedOrder
          suppressRowClickSelection
          suppressRowDeselection
          tableHeaderTitle={intl.formatMessage(labels.tableTitle)}
          tableHeaderCount={rowDataLength}
          tableHeaderContent={
            <StyledTableHeader>
              <VendorsTableHeader />
              <PaymentMethodChips />
            </StyledTableHeader>
          }
          tableHeaderFilters={{
            selectedStore,
            selectedDueDate,
            selectedPaymentMethods
          }}
          totalsInFooter={{
            selectedTotalInFooter,
            discountTotalInFooter,
            totalOwedInFooter
          }}
          useCustomSearchFilter
        />
      </StyledCard>
      <VendorsTotalsFooter data={data} />
    </>
  );
};
